import React, { useEffect, useState, useCallback, useMemo, useRef } from 'react';
import api from '../utils/api';
import Modal from './Modal';
import { useWallet } from '../context/WalletContext';
import { useAuth } from '../context/AuthContext';
import { useNotification } from './Notification';
import { enrichNftBonusesWithClientHolds } from '../utils/tokenHoldings';
import { mergeNftBonusRows } from '../utils/mergeNftBonuses';
import NftHolderBonusesSection from './NftHolderBonusesSection';
import { linkWalletWithProof, isWalletLimitError } from '../utils/walletAuth';
import { freeTicketPeriodShortLabel } from '../utils/freeTicketPeriod';
import { normalizeFreeTicketRequirements, analyzeFreeTicketAffordability, formatFreeEntryRequirement } from '../utils/freeTicketRequirements';
import { computeFreePlayEligibility } from '../utils/freePlayEligibility';
import FreePlayEligibilityPanel from './FreePlayEligibilityPanel';

function SubmitSpinner() {
  return (
    <svg
      className="animate-spin h-4 w-4 shrink-0 text-white"
      xmlns="http://www.w3.org/2000/svg"
      fill="none"
      viewBox="0 0 24 24"
      aria-hidden
    >
      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
      <path
        className="opacity-75"
        fill="currentColor"
        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
      />
    </svg>
  );
}

/**
 * Ticket picker for free predictions — balances, NFT/FT bonus table, connect wallet, stake count.
 */
export default function FreePredictionModal({
  open,
  onClose,
  outcomeLabel,
  outcomeSuffix = '',
  outcomeImage = null,
  mode = 'create',
  existingTicketsStaked = 1,
  minTickets = 1,
  minGoldenTickets = 0,
  onConfirm,
  loading = false,
  initialBalances = null,
  initialNftBonuses = [],
}) {
  const { account, ensureConnected, isConnecting } = useWallet();
  const { user } = useAuth();
  const { showNotification } = useNotification();
  const [balances, setBalances] = useState(initialBalances);
  const [nftBonuses, setNftBonuses] = useState(() =>
    Array.isArray(initialNftBonuses) ? initialNftBonuses : []
  );
  const [stake, setStake] = useState(minTickets);
  const [linkingWallet, setLinkingWallet] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const hydratedRef = useRef(false);
  const initialBalancesRef = useRef(initialBalances);
  const initialNftBonusesRef = useRef(initialNftBonuses);
  initialBalancesRef.current = initialBalances;
  initialNftBonusesRef.current = initialNftBonuses;

  const walletParams = useCallback(() => {
    if (!account) return {};
    return { params: { walletAddress: account } };
  }, [account]);

  const isAddMode = mode === 'add';
  const ticketReqs = normalizeFreeTicketRequirements(minTickets, minGoldenTickets);
  const goldenOnly = ticketReqs.goldenOnly;
  const minStake = isAddMode ? 1 : ticketReqs.minTotalStake;

  const applySnapshot = useCallback((nextBalances, nextBonuses) => {
    if (nextBalances) setBalances(nextBalances);
    if (Array.isArray(nextBonuses) && nextBonuses.length) {
      setNftBonuses((prev) => mergeNftBonusRows(prev, nextBonuses));
    }
  }, []);

  const load = useCallback(
    async ({ forceVerify = false } = {}) => {
      const hasSnapshot = !!(initialBalancesRef.current || (initialNftBonusesRef.current || []).length);
      if (!forceVerify && hasSnapshot && !hydratedRef.current) {
        applySnapshot(initialBalancesRef.current, initialNftBonusesRef.current);
        hydratedRef.current = true;
      }
      const showVerify = forceVerify || (!hydratedRef.current && !hasSnapshot && !!account && !!user);
      if (showVerify) setVerifying(true);
      try {
        if (user) {
          const endpoint = forceVerify && account ? '/tickets/verify-holdings' : '/tickets/balances';
          const { data } = await api.get(endpoint, walletParams());
          setBalances(data);
          if (Array.isArray(data?.nftBonuses) && data.nftBonuses.length) {
            let list = data.nftBonuses;
            if (account && list.length) {
              list = await enrichNftBonusesWithClientHolds(list, account);
            }
            setNftBonuses((prev) => mergeNftBonusRows(prev, list));
          }
          hydratedRef.current = true;
        } else {
          const { data: pub } = await api.get('/tickets/nft-bonuses/config');
          setBalances(null);
          setNftBonuses(Array.isArray(pub?.nftBonuses) ? pub.nftBonuses : []);
        }
      } catch {
        if (!hasSnapshot && user) {
          const golden = Math.max(0, parseInt(user.goldenTickets, 10) || 0);
          const normal = Math.max(0, parseInt(user.tickets, 10) || 0);
          setBalances({
            goldenTickets: golden,
            normalTickets: normal,
            totalSpendable: golden + normal,
          });
        }
      } finally {
        setVerifying(false);
      }
    },
    [user, account, walletParams, applySnapshot]
  );

  useEffect(() => {
    if (open) {
      setStake(minStake);
      if (initialBalancesRef.current) setBalances(initialBalancesRef.current);
      const bonuses = initialNftBonusesRef.current;
      if (Array.isArray(bonuses) && bonuses.length) {
        setNftBonuses((prev) => mergeNftBonusRows(prev, bonuses));
        hydratedRef.current = true;
      }
      load();
    } else {
      hydratedRef.current = false;
    }
  }, [open, minStake, load]);

  useEffect(() => {
    if (!open || !initialBalances) return;
    setBalances(initialBalances);
  }, [open, initialBalances]);

  const total = balances?.totalSpendable ?? 0;
  const normalAvailable = balances?.normalTickets ?? 0;
  const goldenAvailable = balances?.goldenTickets ?? 0;
  const spendable = goldenOnly ? goldenAvailable : total;
  const maxStake = Math.max(minStake, spendable);
  const nftBonusActive = (balances?.nftNormalAvailable || balances?.nftBonusToday || 0) > 0;
  const stakeAfford = analyzeFreeTicketAffordability({
    minFreeTickets: isAddMode ? 0 : ticketReqs.minFreeTickets,
    minGoldenTickets: isAddMode ? (goldenOnly ? stake : 0) : ticketReqs.minGoldenTickets,
    normalHave: normalAvailable,
    goldenHave: goldenAvailable,
    stake,
  });
  const meetsEntryReqs = !user || stakeAfford.ok;

  const eligibility = useMemo(
    () =>
      computeFreePlayEligibility({
        user,
        account,
        balances,
        balancesLoading: verifying && !balances,
        minFreeTickets: minTickets,
        minGoldenTickets,
        locked: false,
        isResolved: false,
        nftBonuses,
        mode,
      }),
    [user, account, balances, verifying, minTickets, minGoldenTickets, nftBonuses, mode]
  );

  const confirmDisabledReason = useMemo(() => {
    if (loading) return null;
    if (!user) return 'Log in to confirm your pick';
    if (!stakeAfford.ok) return stakeAfford.message;
    return null;
  }, [loading, user, stakeAfford]);

  const dec = () => setStake((s) => Math.max(minStake, s - 1));
  const inc = () => setStake((s) => Math.min(maxStake, s + 1));
  const setMax = () => setStake(maxStake);

  const handleConnectWallet = async () => {
    if (!user) {
      showNotification('Please log in first', 'warning');
      return;
    }
    setLinkingWallet(true);
    try {
      const addr = account || (await ensureConnected());
      if (!addr) return;
      await linkWalletWithProof(addr);
      showNotification('Wallet linked — verifying holdings on-chain…', 'success');
      await load({ forceVerify: true });
    } catch (e) {
      if (!isWalletLimitError(e)) {
        showNotification(e.response?.data?.message || e.message || 'Could not connect wallet', 'error');
      }
    } finally {
      setLinkingWallet(false);
    }
  };

  const pickLabel = `${outcomeLabel || 'Confirm'}${outcomeSuffix || ''}`;

  const confirmButtonLabel = useMemo(() => {
    if (isAddMode) return `Add ${stake} ticket${stake === 1 ? '' : 's'}`;
    return `Confirm ${pickLabel}`;
  }, [isAddMode, stake, pickLabel]);

  const loadingButtonLabel = isAddMode ? 'Adding tickets…' : 'Submitting prediction…';

  if (!open) return null;

  return (
    <Modal isOpen={open} onClose={onClose} title={isAddMode ? 'Add tickets' : 'Free prediction'} size="lg">
      <div className="space-y-5 text-sm">
        {pickLabel && (
          <div className="rounded-lg border border-slate-200 dark:border-slate-600 bg-slate-50 dark:bg-slate-800/50 px-4 py-3">
            <div className="text-xs font-semibold uppercase tracking-wide text-slate-500 mb-1">Your pick</div>
            <div className="font-semibold text-slate-900 dark:text-white">{pickLabel}</div>
            {isAddMode && (
              <div className="text-xs text-slate-500 dark:text-slate-400 mt-1">
                Currently staked: {Math.max(1, parseInt(existingTicketsStaked, 10) || 1)} ticket
                {(Math.max(1, parseInt(existingTicketsStaked, 10) || 1)) === 1 ? '' : 's'}
              </div>
            )}
          </div>
        )}

        <div>
          <h3 className="text-xs font-semibold uppercase tracking-wide text-slate-500 mb-2">Your tickets</h3>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
            <div className="rounded-lg border border-slate-200 dark:border-slate-600 p-3 flex items-center gap-3">
              <span className="text-2xl shrink-0" aria-hidden>
                🎟️
              </span>
              <div>
                <div className="text-xs text-slate-500">
                  {freeTicketPeriodShortLabel(balances?.periodDays)}
                </div>
                <div className="font-bold text-lg tabular-nums">{user ? (balances?.normalTickets ?? '…') : '—'}</div>
              </div>
            </div>
            <div className="rounded-lg border border-amber-300/60 dark:border-amber-700 p-3 flex items-center gap-3 bg-amber-50/50 dark:bg-amber-950/30">
              <span className="text-2xl shrink-0" aria-hidden>
                ⭐
              </span>
              <div>
                <div className="text-xs text-slate-500">Golden</div>
                <div className="font-bold text-lg tabular-nums text-amber-800 dark:text-amber-200">
                  {user ? (balances?.goldenTickets ?? '…') : '—'}
                </div>
              </div>
            </div>
            <div className="rounded-lg border border-emerald-300/60 dark:border-emerald-700 p-3 bg-emerald-50/50 dark:bg-emerald-950/30">
              <div className="text-xs text-slate-500">Total available</div>
              <div className="font-bold text-lg tabular-nums text-emerald-800 dark:text-emerald-200">
                {user ? total : '—'}
              </div>
              {nftBonusActive && (
                <div className="text-[10px] text-emerald-700 dark:text-emerald-400 mt-0.5">
                  includes +{balances.nftNormalAvailable ?? balances.nftBonusToday} NFT normal bonus
                </div>
              )}
            </div>
          </div>
        </div>

        <NftHolderBonusesSection
          nftBonuses={nftBonuses}
          user={user}
          account={account}
          verifying={verifying}
          onConnectWallet={handleConnectWallet}
          linkingWallet={linkingWallet}
          isConnecting={isConnecting}
        />

        {!isAddMode ? (
          <FreePlayEligibilityPanel
            eligibility={eligibility}
            loading={verifying && !balances}
            compact
            onConnectWallet={handleConnectWallet}
            connectingWallet={linkingWallet || isConnecting}
          />
        ) : null}

        {isAddMode && spendable < 1 ? (
          <p className="text-xs text-red-700 dark:text-red-300 rounded-lg border border-red-200 dark:border-red-900 px-3 py-2">
            {goldenOnly
              ? 'This event is golden-ticket only. You need at least 1 golden ticket to add to your pick.'
              : 'You need at least 1 ticket to add to your pick. Golden tickets can be used if your free tickets are exhausted.'}
          </p>
        ) : null}

        <div>
          <label className="block text-xs font-semibold uppercase tracking-wide text-slate-500 mb-2">
            {isAddMode ? `Tickets to add (min 1)` : `Tickets for this pick (min ${minStake})`}
          </label>
          <p className="text-xs text-slate-500 dark:text-slate-400 mb-2">
            More tickets = larger jackpot share if you win (weighted by tickets staked).{' '}
            {formatFreeEntryRequirement(ticketReqs)}
          </p>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={dec}
              disabled={loading || stake <= minStake}
              className="w-10 h-10 rounded-lg border border-slate-300 dark:border-slate-600 font-bold disabled:opacity-40"
            >
              −
            </button>
            <span className="flex-1 text-center text-xl font-bold tabular-nums">{stake}</span>
            <button
              type="button"
              onClick={inc}
              disabled={loading || !user || stake >= maxStake}
              className="w-10 h-10 rounded-lg border border-slate-300 dark:border-slate-600 font-bold disabled:opacity-40"
            >
              +
            </button>
            <button
              type="button"
              onClick={setMax}
              disabled={loading || !user || maxStake < minStake}
              className="px-3 py-2 rounded-lg border border-slate-300 dark:border-slate-600 text-xs font-semibold disabled:opacity-40"
            >
              Max
            </button>
          </div>
        </div>

        {confirmDisabledReason ? (
          <p className="text-xs text-amber-800 dark:text-amber-200 bg-amber-50 dark:bg-amber-950/40 border border-amber-200 dark:border-amber-800 rounded-lg px-3 py-2">
            {confirmDisabledReason}
          </p>
        ) : null}

        <div className="flex gap-2 pt-1">
          <button
            type="button"
            onClick={onClose}
            disabled={loading}
            className="flex-1 py-2.5 rounded-lg border border-slate-300 dark:border-slate-600 disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            type="button"
            disabled={loading || !user || !meetsEntryReqs}
            onClick={() => onConfirm(stake)}
            className="flex-1 py-2.5 rounded-lg bg-emerald-600 text-white font-semibold disabled:opacity-50 hover:bg-emerald-700 inline-flex items-center justify-center gap-2 min-h-[44px]"
          >
            {loading ? (
              <>
                <SubmitSpinner />
                <span>{loadingButtonLabel}</span>
              </>
            ) : (
              confirmButtonLabel
            )}
          </button>
        </div>
      </div>
    </Modal>
  );
}
